import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import HistoryEduRoundedIcon from "@mui/icons-material/HistoryEduRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import ReactGA from "react-ga4";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

export default function ChorusLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/learning/chorus",
      title: "Chorus Learning",
    });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "What Is Chorus in Music and Audio Production?",
      description:
        "Learn what the chorus effect is, how it works, where it appears naturally, its history, common controls, and practical ways to use it in music production.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/chorus",
      articleSection: "Audio Education",
      keywords: [
        "chorus effect",
        "audio production",
        "music production",
        "chorus plugin",
        "modulation effect",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Chorus thickens a sound by blending it with slightly delayed and pitch-modulated copies.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it shines",
      text: "Guitars, synths, clean keys, background vocals, pads, and stereo widening.",
      icon: <HeadphonesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Core controls",
      text: "Rate, depth, delay time, mix, feedback, and stereo spread.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const controls = [
    {
      name: "Rate",
      description:
        "Controls how quickly the modulation moves. Lower settings feel smooth and wide, while higher settings sound more animated or warbly.",
    },
    {
      name: "Depth",
      description:
        "Sets how strong the pitch modulation is. More depth usually means a bigger, more obvious chorus effect.",
    },
    {
      name: "Delay Time",
      description:
        "Changes the offset between the dry signal and the delayed copies. Shorter times feel tighter; longer times can sound more spacious.",
    },
    {
      name: "Mix",
      description:
        "Balances the dry signal with the chorused signal. Lower mix settings are subtle, while higher settings push the effect forward.",
    },
    {
      name: "Feedback",
      description:
        "Feeds some of the processed signal back into the effect for a thicker or more pronounced texture.",
    },
    {
      name: "Stereo Spread / Phase",
      description:
        "Offsets modulation between the left and right channels to create more width and movement in stereo.",
    },
    {
      name: "Voices",
      description:
        "Some chorus units provide multiple voices, stacking several modulated copies for a richer ensemble-style sound.",
    },
  ];

  const bestPractices = [
    "Use subtle settings on vocals to avoid a seasick or overly synthetic sound.",
    "Chorus works especially well on clean guitars, synth pads, keys, and background textures.",
    "Pair chorus with delay and reverb for extra size and depth.",
    "Be careful on bass-heavy material, since chorus can create phase blur and mud in the low end.",
    "Stereo chorus can sound huge, but always check mono compatibility if the track may be summed.",
  ];

  return (
    <>
      <Seo
        title="What Is Chorus? | Parish Audio Learning"
        description="Learn what the chorus effect is, how it works, its history, common controls, and best practices for using chorus in music production."
        canonical="https://parishaudio.com/learning/chorus"
        schema={articleSchema}
      />

      <Box sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
          }}
        >
          <Stack spacing={3}>
            <Box ref={topContainer}>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                }}
              >
                Chorus
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.12rem" },
                  lineHeight: 1.75,
                  maxWidth: 900,
                }}
              >
                Chorus is one of the most recognizable modulation effects in music
                production. It can make a sound feel wider, richer, and more animated
                by blending the original signal with subtly delayed and pitch-shifted
                copies. In practice, chorus can range from a gentle sense of polish to
                a lush, swirling texture that completely changes the character of a sound.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: panelBg,
                      boxShadow: 6,
                      width: "100%",
                      minHeight: 75,
                      overflow: "hidden",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ overflow: "hidden" }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.45,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: panelBg,
                boxShadow: 8,
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                  <Typography
                    variant="h2"
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    What Is the Chorus Effect?
                  </Typography>
                </Stack>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Chorus happens when similar versions of the same sound are combined
                  with tiny differences in timing and pitch. In natural settings, that
                  could be multiple singers performing the same line, or several string
                  players landing on the same note with slight variations. In audio
                  processing, chorus recreates that effect electronically by delaying
                  and modulating copies of a signal, then blending them back in with
                  the original.
                </Typography>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  The result is often described as lush, shimmering, thick, or wide.
                  Used tastefully, it can make a part sound more expensive and polished.
                  Used aggressively, it can become dreamy, seasick, or obviously
                  synthetic.
                </Typography>
              </Stack>
            </Paper>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Natural Occurrence of Chorus
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Chorus happens naturally whenever multiple similar sound sources
                      are heard together.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Choirs and orchestras:</strong>{" "}
                        small differences in timing, phrasing, and pitch create a
                        naturally rich ensemble sound.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Pianos:</strong>{" "}
                        multiple strings per note are slightly detuned to create depth
                        and liveliness.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>12-string guitars:</strong>{" "}
                        doubled strings create a built-in chorus-like shimmer.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Accordions and organs:</strong>{" "}
                        slight variations between reeds or pipes create a lively,
                        animated texture.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>
                          Mandolins, lutes, dulcimers, and santurs:
                        </strong>{" "}
                        multiple strings per pitch naturally create subtle chorusing.
                      </li>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How Digital Chorus Works
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>Duplicate the signal:</strong>{" "}
                        one or more copies are created.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Delay the copies:</strong>{" "}
                        they are offset by a few milliseconds, often around 5–30 ms.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Modulate the delay:</strong>{" "}
                        a low-frequency oscillator changes the delay slightly over time,
                        which produces gentle pitch movement.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>
                          Blend with the dry signal:
                        </strong>{" "}
                        the moving copies are mixed back in, creating width and motion.
                      </li>
                    </Box>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Stereo chorus often uses different modulation on the left and right
                      channels, which makes the effect feel bigger and more immersive.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: panelBg,
                boxShadow: 8,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <HistoryEduRoundedIcon sx={{ color: accent }} />
                  <Typography
                    variant="h2"
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    A Brief History of Chorus
                  </Typography>
                </Stack>

                <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                  Chorus has roots in both acoustic ensembles and early electronic
                  instrument design. Over time, it evolved from mechanical and tape-based
                  processes into analog pedals, rack units, and modern software plugins.
                </Typography>

                <Stack spacing={2.25}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                      Early origins: Hammond organs
                    </Typography>
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      The Hammond organ introduced a mechanical chorus-style sound
                      through scanner vibrato, an electromechanical system that helped
                      produce a warm, moving texture.
                    </Typography>

                  </Box>

                  <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                      The 1960s: tape-based chorus and ADT
                    </Typography>
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Studio engineers experimented with automatic double tracking
                      and tape delay offsets, which helped shape the sound of early
                      artificial chorus-style widening.
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <iframe
                        width="100%"
                        height="315"
                        style={{ maxWidth: "560px", border: 0, borderRadius: "10px" }}
                        src="https://www.youtube.com/embed/hGJ9qXXMZMQ?si=7Ob1XP_ZWS769bwW"
                        title="Automatic Double Tracking history"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                      The 1970s: analog chorus pedals and amps
                    </Typography>
                    <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 1.25 }}>
                      The 1970s brought bucket brigade device designs into chorus
                      pedals, amps, and rack units.
                    </Typography>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.2, lineHeight: 1.7 },
                      }}
                    >
                      <li>
                        <strong style={{ color: style.color4 }}>
                          Roland Jazz Chorus amplifiers:
                        </strong>{" "}
                        famous for their built-in chorus and spacious clean sound.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>Boss CE-1:</strong>{" "}
                        one of the earliest dedicated chorus pedals and a landmark
                        effect design.
                      </li>
                      <li>
                        <strong style={{ color: style.color4 }}>
                          Electro-Harmonix Small Clone:
                        </strong>{" "}
                        later became a classic chorus sound in alternative and grunge music.
                      </li>
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 0.25 }}>


                      <Grid item xs={12} md={6}>
                        <Box sx={{ maxWidth: 280 }}>
                          <Box
                            component="a"
                            href="https://www.boss.info/us/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: "block", textDecoration: "none" }}
                          >
                            <Box
                              component="img"
                              src="/BossC1.png"
                              alt="Boss CE-1 Chorus pedal on a white background"
                              sx={{
                                width: "100%",
                                borderRadius: 2,
                                display: "block",
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              mt: 1,
                              fontSize: "0.95rem",
                              color: style.color3,
                              textAlign: "center",
                            }}
                          >
                            Boss CE-1 Chorus Pedal
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5 }}>
                      The 1980s and beyond
                    </Typography>
                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      As digital processing improved, chorus found its way into
                      synthesizers, rack processors, and eventually software plugins.
                      Synths like the Roland Juno series helped define the lush, wide
                      chorus textures that became iconic in 1980s music.
                    </Typography>

                  </Box>
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={2.25} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Chorus Parameters and Controls
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {controls.map((control) => (
                        <Paper
                          key={control.name}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: darkPanel,
                            boxShadow: 3,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.4 }}>
                            {control.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {control.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    backgroundColor: panelBg,
                    boxShadow: 8,
                    width: "100%",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                      Related topics:
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        component={RouterLink}
                        to="/learning/delay"
                        variant="outlined"
                        sx={{
                          borderColor: accent,
                          color: style.color4,
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.04)",
                          },
                        }}
                      >
                        Delay
                      </Button>
                      <Button
                        component={RouterLink}
                        to="/learning/reverb"
                        variant="outlined"
                        sx={{
                          borderColor: accent,
                          color: style.color4,
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.04)",
                          },
                        }}
                      >
                        Reverb
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.03)",
                boxShadow: 4,
              }}
            >
              <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                * Parish Audio is not affiliated with Hammond, Roland, Boss,
                Electro-Harmonix, Abbey Road, or any other brand mentioned on
                this page. Brand names are referenced only for educational and
                historical context.
              </Typography>
            </Paper>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}
// import {React, useState, useContext, useEffect, useRef} from "react"
// import {useStyle} from '../../context/styleContext/styleContext'
// import ReactGA from 'react-ga4';
// import { Paper } from '@mui/material';
// import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
// import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";


// export default function ChorusLearning(){

//   const { style, setStyle } = useStyle();
//   const [width, setWidth] = useState(window.innerWidth)

// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;

//   const [height, setHeight] = useState(window.innerHeight)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

// const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);




//     return(
//         <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

//         <h1 style={{marginTop:'25px', color:'white'}} ref={topContainer} >Chorus</h1>
//         <Divider className="mobile-card-divider" />
//         <p>The chorus effect is an essential tool in audio production, widely used to create depth, richness, and spatial enhancement in music. Whether naturally occurring in ensembles like choirs and orchestras or artificially generated through electronic processing, chorus plays a vital role in shaping the texture and character of sound.</p>
//         <h2 style={{color:'white'}}>What is the Chorus Effect?</h2>
//         <p>Chorus is an audio effect that occurs when multiple sound sources with similar pitches and timings are combined, creating a wider, richer sound. It is often described as a shimmering or thickening effect, achieved by slightly modulating the pitch and timing of duplicated signals. The result mimics the natural variations that occur in ensembles of singers or instrumentalists playing the same notes. The effect is used in a wide variety of music genres, from rock and pop to electronic and orchestral compositions.</p>
//         <h2 style={{marginTop:'25px', color:'white'}}>Natural Occurrence of Chorus</h2>
//         <p>The chorus effect occurs naturally in various musical settings:</p>
//         <ul class="a">
//             <li><strong style={{color:'white'}}>Choirs and Orchestras: </strong>In a choir, multiple singers performing the same part create a slight variation in pitch and timing, which enriches the overall sound. Similarly, in a string orchestra, the slight detuning of multiple violins playing the same note produces a warm, full tone.</li>
//             <li><strong style={{color:'white'}}>Acoustic Instruments:</strong>Certain instruments inherently produce a chorus-like effect due to their design. Examples include:
//             <ul class="a">
//                 <li><strong style={{color:'white'}}>Pianos: </strong>Multiple strings per note are slightly detuned to enhance tonal richness.</li>
//                 <li><strong style={{color:'white'}}>12-String Guitars: </strong>The paired strings, tuned in octaves or unison, create a natural chorusing effect.</li>
//                 <li><strong style={{color:'white'}}>Accordions and Pipe Organs: </strong>Reed and pipe variations create a lush, vibrating sound characteristic of the chorus effect.</li>
//                 <li><strong style={{color:'white'}}>Santur and Dulcimers: </strong>These hammered string instruments naturally produce a chorus-like shimmer due to their multiple course string setups.</li>
//                 <li><strong style={{color:'white'}}>Mandolin and Lute: </strong>Double-strung instruments that create natural chorusing by pairing strings in unisons or octaves.</li>
//             </ul>
//             </li>
//         </ul>
//         <h2 style={{marginTop:'25px', color:'white' }}>Digital Chorus</h2>
//       <p>While natural chorus effects occur in ensembles and specific instruments, digital chorus processing has been developed to replicate and enhance this phenomenon artificially.</p>
//       <h3 style={{color:'white'}}>How Digital Chorus Works</h3>
//       <p>Digital chorus effects are achieved by:</p>
//       <ul class="a">
//         <li><strong style={{color:'white'}}>Duplicating the Original Signal: </strong>A copy (or multiple copies) of the original signal is created.</li>
//         <li><strong style={{color:'white'}}>Delaying the Copies: </strong>The duplicated signals are delayed by a few milliseconds (typically 5-30ms).</li>
//         <li><strong style={{color:'white'}}>Modulating the Pitch: </strong>The pitch of the delayed signals is modulated using a low-frequency oscillator (LFO) to create a slight fluctuation.</li>
//         <li><strong style={{color:'white'}}>Mixing with the Original Signal: </strong>The modulated copies are blended back with the original sound, producing the characteristic chorus effect.</li>

//       </ul>
//       <h3 style={{color:'white'}}>Mono vs. Stereo Chorus</h3>
//       <ul class="a">
//         <li><strong style={{color:'white'}}>Mono Chorus: </strong>A single-channel effect where modulation occurs uniformly across the audio signal.</li>
//         <li><strong style={{color:'white'}}>Stereo Chorus: </strong>Varies modulation between the left and right channels, creating a more immersive and spacious effect.</li>
//         <li><strong style={{color:'white'}}>Ensemble Simulation </strong>Some advanced chorus effects attempt to simulate a full ensemble by applying multiple modulated delay lines to create a more complex texture.</li>   
//       </ul>
//     <h2 style={{marginTop:'25px', color:'white'}}>History</h2>
//     <h3 style={{marginBottom:'2px', color:'white'}}>Early Origins: The Hammond Organ</h3>
//     <p style={{marginTop:'5px'}}>The <a href="https://hammondorganco.com/" target="_blank" rel="noopener noreferrer"><strong>Hammond</strong></a> organ, first introduced in the 1930s, featured a mechanical chorus effect known as scanner vibrato. This system used an electromechanical rotor to modulate pitch, producing a warm and wavy sound. It was one of the earliest electronic implementations of the chorus effect.</p>
//     <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>
//       <WikiCommonsImage width="100%" bottomText="1958 Hammond B-3 Tone Wheel Organ" bottomTextLinkUrl="https://hammondorganco.com/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Hammond_B-3_(1958)_tone-wheel_organ_-_MIM_Phoenix_AZ_(2017-12-04_14.14.22_by_bobistraveling_141422).jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/thumb/d/dc/Hammond_B-3_%281958%29_tone-wheel_organ_-_MIM_Phoenix_AZ_%282017-12-04_14.14.22_by_bobistraveling_141422%29.jpg/512px-Hammond_B-3_%281958%29_tone-wheel_organ_-_MIM_Phoenix_AZ_%282017-12-04_14.14.22_by_bobistraveling_141422%29.jpg?20180527025133" alt = "1958 Hammond B-3 Tone Wheel Organ"  title = "bobistraveling, CC BY 2.0 &lt;https://creativecommons.org/licenses/by/2.0&gt;, via Wikimedia Commons" />
//       </div>
//       <h3 style={{marginBottom:'2px', marginTop:'40px', color:'white'}}>The 1960s: Tape-Based Chorus and ADT</h3>
//     <p style={{marginTop:'5px'}}>In the 1960s, studio engineers began experimenting with Automatic Double Tracking (ADT), a technique that involved recording a performance onto two tape machines with a slight delay between them. The Beatles famously used <a href="https://www.abbeyroad.com/news/inside-abbey-road-artificial-double-tracking-2530" target="_blank" rel="noopener noreferrer">ADT</a> at <a href="https://www.abbeyroad.com/" target="_blank" rel="noopener noreferrer"><strong>Abbey Road Studios</strong></a>, which led to the development of dedicated chorus effects.</p>

//       <iframe width="95%" height='275px' style={{maxWidth:'520px', marginLeft:'5%', marginRight:'5px'}} src="https://www.youtube.com/embed/hGJ9qXXMZMQ?si=7Ob1XP_ZWS769bwW" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>

//       <h3 style={{marginBottom:'2px', marginTop:'60px', color:'white'}}>The 1970s: Analog Chorus Pedals and Rack Units</h3>
//     <p style={{marginTop:'5px'}}>The 1970s saw the rise of analog chorus units utilizing Bucket Brigade Devices (BBDs). Some notable developments include:</p>
//     <ul class="a" style={{marginTop:'25px'}}>
//         <li style={{}}><strong style={{color:'white'}}><a href="https://www.roland.com/global/products/jc-120/" target="_blank" rel="noopener noreferrer">Roland Jazz Chorus Amplifiers (1975):</a></strong>These solid-state amplifiers featured a built-in chorus circuit, which became legendary for its lush, spatial quality.
//         <br />
//         <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>

//         <WikiCommonsImage width="100%" bottomText="Roland Jazz Chorus" bottomTextLinkUrl="https://www.roland.com/global/products/jc-120/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:JC-120UT.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/JC-120UT.jpg/512px-JC-120UT.jpg?20060830114243" alt = "JC-120UT"  title = "Roland Musikvertrieb Deutschland, CC BY-SA 3.0 &lt;http://creativecommons.org/licenses/by-sa/3.0/&gt;, via Wikimedia Commons" />
// </div>
//         </li>
//         <li style={{marginTop:'50px'}}><strong style={{color:'white'}}><a href="https://www.boss.info/us/" target="_blank" rel="noopener noreferrer">Boss CE-1 (1976): </a></strong>The first dedicated chorus pedal, derived from the circuitry of the Roland Jazz Chorus amp, became a staple for guitarists.</li>
//         <div style={{marginTop:'15px', marginBottom:'15px', paddingTop:'10px', display:'flex', flexDirection:'column', width:'95%', maxWidth:'275px', marginLeft:'50px'}}>
//           <a style={{margin:'auto'}} title="Boss CE-1 Chorus Pedal" href="https://www.boss.info/us/"><img width="256" style={{margin:'auto', borderRadius:'5px'}} alt="Boss CE-1 Chorus pedal on a white background" src="/BossC1.png" /></a>
//           <span style={{textAlign:"center"}}><a href="https://www.boss.info/us/" target="_blank" rel="noopener noreferrer">Boss CE-1 Chorus Pedal</a></span>
//         </div>
//         <li style={{marginTop:'50px'}}><strong style={{color:'white'}}><a href="https://www.ehx.com/products/small-clone/" target="_blank" rel="noopener noreferrer">Electro-Harmonix Small Clone: </a> </strong>Later popularized by Kurt Cobain, this chorus pedal became an iconic effect in the grunge era.</li>
//         <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'160px', textAlign:'left'}}>
        
//         <WikiCommonsImage width="150px" bottomText="Electro-Harmonix Small Clone" bottomTextLinkUrl="https://www.ehx.com/products/small-clone/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Electro-Harmonix_SmallClone_Chorus_GuitarEffect_1189-2.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/1/1b/Electro-Harmonix_SmallClone_Chorus_GuitarEffect_1189-2.jpg?20110501171019" alt = "Electro-Harmonix Small Clone Chorus effects pedal on a white background"  title = "Electro-Harmonix_SmallClone_Chorus_GuitarEffect_1189.jpg: Sky99 derivative work: Atlantictire, CC BY-SA 3.0 &lt;https://creativecommons.org/licenses/by-sa/3.0&gt;, via Wikimedia Commons"/>
//        </div>
//         </ul>
//     <h3 style={{marginBottom:'2px', marginTop:'50px', color:'white'}}>The 1980s: Digital Chorus and Synthesizers</h3>
//     <p style={{marginTop:'5px'}}>As digital technology advanced, chorus effects were incorporated into synthesizers and digital multi-effects units. The Roland Juno-6 and <a href="https://www.roland.com/us/products/rc_juno-60/" target="_blank" rel="noopener noreferrer"> Juno-60</a> synthesizers included built-in chorus, defining the lush, wide textures of 1980s synth music.</p>
//     <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'600px', textAlign:'left'}}>
//     <WikiCommonsImage width="100%" bottomText="Roland Juno-60 Synthesizer" bottomTextLinkUrl="https://www.roland.com/us/products/rc_juno-60/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Roland_Juno-60.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a6/Roland_Juno-60.jpg/512px-Roland_Juno-60.jpg?20110406115855" alt = "Roland Juno-60 Synthesizer" title="Taavo_from_Stockholm_and_Ivana_Helsinki.jpg: Ville Hyvönen from helsinki / tallinn, Finland
// derivative work: Shoulder-synth, CC BY-SA 2.0 &lt;https://creativecommons.org/licenses/by-sa/2.0&gt;, via Wikimedia Commons" />
//     </div>
//     <h3 style={{marginBottom:'2px', marginTop:'50px', color:'white'}}>Modern Era: Digital and Software-Based Chorus</h3>
//     <p style={{marginTop:'5px'}}>Today, chorus effects are available as software plugins, digital pedals, and rack processors, offering precise control over modulation parameters, stereo imaging, and frequency shaping.</p>
// <h2 style={{marginTop:'60px', color:'white'}}>Chorus Effect Parameters and Controls</h2>
// <p>Understanding the key parameters of a chorus unit helps in shaping the desired sound: </p>
// <ul class="a">
//     <li><strong style={{color:'white'}}>Rate (Speed): </strong>Controls the speed of LFO modulation, affecting how quickly the pitch varies.</li>
//     <li><strong style={{color:'white'}}>Depth: </strong>Determines the intensity of pitch modulation.</li>
//     <li><strong style={{color:'white'}}>Delay Time: </strong>Adjusts the time offset between the original and delayed signals.</li>
//     <li><strong style={{color:'white'}}>Mix (Wet/Dry Ratio): </strong>Balances the amount of chorused signal blended with the dry signal.</li>
//     <li><strong style={{color:'white'}}>Feedback: </strong>Routes some of the output back into the input to create a more pronounced effect.</li>
//     <li><strong style={{color:'white'}}>Phase Offset: </strong>Used in stereo chorus to shift the phase between left and right channels, enhancing spatial depth.</li>
//     <li><strong style={{color:'white'}}>Multi-Voice Mode: </strong>Some chorus units allow for multiple voices to be layered, simulating a richer ensemble.</li>
// </ul>
//       <h2 style={{marginTop:'25px', color:'white'}}>Best Practices</h2>
//         <ul class="a" style={{marginBottom:'30px'}}>
//             <li><strong style={{color:'white'}}>Use Subtly on Vocals: </strong>Too much chorus can make vocals sound unnatural. </li>
//             <li><strong style={{color:'white'}}>Pair with <a href="/learning/delay">Delay</a> and <a href="/learning/reverb"><strong>Reverb: </strong></a> </strong>Enhances spatial depth and stereo width. </li>
//             <li><strong style={{color:'white'}}>Experiment with Stereo Imaging:  </strong>Stereo chorus can create immersive, spacious effects.</li>
//             <li><strong style={{color:'white'}}>Avoid Overuse on Low Frequencies: </strong>Can create phase issues and muddiness. </li>
//         </ul>
//         <br />
//         <br />
//         <br />
//         <br />

//         <p>* We are not affiliated with <strong><a href="https://hammondorganco.com/" target="_blank" rel="noopener noreferrer">Hammond,</a> <a href="https://www.roland.com/" target="_blank" rel="noopener noreferrer">Roland,</a> <a href="https://www.boss.info/" target="_blank" rel="noopener noreferrer">Boss, </a><a href="https://www.ehx.com/" target="_blank" rel="noopener noreferrer">Electro-Harmonix</a></strong>, or any other brand mentioned on this site</p>
      
//       </div>
//        </>

//     )
// }

